import { useState, useEffect, useCallback } from 'react';
import { fetchProjectChannelMetrics } from '@/service/api/revenue';
import DatePicker from '@/components/common/DatePicker';
import { formatDate, formatNumber } from './utils';

interface MetricsTabProps {
  project: Api.Project.ProjectDetailResponse;
  onRefresh?: () => void;
}

export default function MetricsTab({ project }: MetricsTabProps) {
  const projectId = project.id || '';
  const channels = project.channels || [];

  const [selectedChannelId, setSelectedChannelId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [episodeNo, setEpisodeNo] = useState<string>('');

  const [metrics, setMetrics] = useState<Api.Revenue.MetricResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleStartDateChange = (val: string) => {
    setStartDate(val);
    if (val && endDate && val > endDate) {
      setEndDate('');
    }
  };

  const handleEndDateChange = (val: string) => {
    setEndDate(val);
    if (val && startDate && val < startDate) {
      setStartDate('');
    }
  };

  // 默认选中第一个有效渠道标识
  useEffect(() => {
    if (channels.length > 0 && !selectedChannelId) {
      const firstChannelId = channels[0].id || channels[0].channel_id || '';
      if (firstChannelId) {
        setSelectedChannelId(firstChannelId);
      }
    }
  }, [channels, selectedChannelId]);

  const loadMetrics = useCallback(async () => {
    if (!projectId || !selectedChannelId) return;
    setIsLoading(true);
    setError('');
    try {
      const params: Api.Revenue.ProjectChannelMetricsParams = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (episodeNo) params.episode_no = Number(episodeNo);

      const res = await fetchProjectChannelMetrics(projectId, selectedChannelId, params);
      setMetrics(res || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取渠道运营指标失败');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, selectedChannelId, startDate, endDate, episodeNo]);

  useEffect(() => {
    if (selectedChannelId) {
      loadMetrics();
    }
  }, [selectedChannelId, loadMetrics]);

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setEpisodeNo('');
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h3 className="font-semibold text-foreground-950 text-sm">项目渠道运营指标数据</h3>
        <p className="text-xs text-foreground-500 mt-1">
          查询各发行渠道的具体运营指标明细（播放量、互动量、完播率等）
        </p>
      </div>

      {channels.length === 0 ? (
        <div className="py-12 text-center text-foreground-400 text-sm glass-card rounded-xl">
          <i className="ri-bar-chart-grouped-line text-3xl block mb-2 text-foreground-300"></i>
          当前项目暂无分配发行渠道，无法查询渠道运营指标数据
        </div>
      ) : (
        <div>
          {/* 渠道 Tabs 切换 */}
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1 border-b border-background-200/50">
            <span className="text-xs text-foreground-500 font-medium whitespace-nowrap">发行渠道:</span>
            {channels.map((ch, idx) => {
              const chId = ch.id || ch.channel_id || String(idx);
              const channelName = ch.channel?.name || `渠道 ${idx + 1}`;
              const isSelected = selectedChannelId === chId;
              return (
                <button
                  key={chId}
                  onClick={() => setSelectedChannelId(chId)}
                  className={`text-xs px-3.5 py-1.5 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${isSelected
                      ? 'bg-primary-500 text-foreground-950 shadow-sm font-semibold'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                    }`}
                >
                  {channelName}
                  {ch.region?.name ? ` (${ch.region.name})` : ''}
                </button>
              );
            })}
          </div>

          {/* 筛选参数栏 */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6 bg-background-50/60 p-4 rounded-xl border border-background-200/50 text-xs">
            <div>
              <label className="block text-foreground-500 text-[11px] font-medium mb-1">起始日期</label>
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                placeholder="选择起始日期"
                maxDate={endDate || undefined}
                size="sm"
                clearable
              />
            </div>
            <div>
              <label className="block text-foreground-500 text-[11px] font-medium mb-1">截止日期</label>
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                placeholder="选择截止日期"
                minDate={startDate || undefined}
                size="sm"
                clearable
              />
            </div>
            <div>
              <label className="block text-foreground-500 text-[11px] font-medium mb-1">单集编号</label>
              <input
                type="number"
                placeholder="例如: 1"
                value={episodeNo}
                onChange={(e) => setEpisodeNo(e.target.value)}
                className="w-full h-9 px-3 rounded-md bg-white border border-background-300/60 text-foreground-800 text-xs focus:outline-none focus:border-primary-500/80 focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={loadMetrics}
                disabled={isLoading}
                className="flex-1 py-1.5 px-4 bg-primary-500 text-foreground-950 rounded-lg font-medium hover:bg-primary-400 transition-all cursor-pointer text-xs shadow-sm disabled:opacity-50"
              >
                {isLoading ? '查询中...' : '查询指标'}
              </button>
              <button
                onClick={handleReset}
                className="py-1.5 px-3 bg-gray-100 text-foreground-600 rounded-lg font-medium hover:bg-gray-200 transition-all cursor-pointer text-xs"
              >
                重置
              </button>
            </div>
          </div>

          {/* 数据加载状态 */}
          {isLoading && (
            <div className="py-16 text-center text-xs text-foreground-400">
              <div className="inline-block w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p>正在获取渠道运营指标数据...</p>
            </div>
          )}

          {/* 错误提示 */}
          {!isLoading && error && (
            <div className="py-10 text-center text-xs text-rose-500 glass-card rounded-xl">
              <i className="ri-error-warning-line text-2xl block mb-2"></i>
              <p className="mb-3 font-medium">{error}</p>
              <button
                onClick={loadMetrics}
                className="px-4 py-1.5 bg-rose-100 text-rose-700 rounded-lg text-xs font-medium hover:bg-rose-200 transition-all cursor-pointer"
              >
                重新加载
              </button>
            </div>
          )}

          {/* 无数据 */}
          {!isLoading && !error && metrics.length === 0 && (
            <div className="py-12 text-center text-xs text-foreground-400 glass-card rounded-xl">
              <i className="ri-inbox-line text-3xl block mb-2 text-foreground-300"></i>
              暂无符合条件的渠道运营指标记录
            </div>
          )}

          {/* 指标数据表格 */}
          {!isLoading && !error && metrics.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-background-200/70 bg-gray-50/60 text-foreground-500 font-medium">
                    <th className="py-3 px-4">统计日期</th>
                    <th className="py-3 px-4">单集编号</th>
                    <th className="py-3 px-4">地区</th>
                    <th className="py-3 px-4 text-right">播放量</th>
                    <th className="py-3 px-4 text-right">点赞数</th>
                    <th className="py-3 px-4 text-right">评论数</th>
                    <th className="py-3 px-4 text-right">收藏数</th>
                    <th className="py-3 px-4 text-right">转化数</th>
                    <th className="py-3 px-4 text-right">完播率</th>
                    <th className="py-3 px-4">数据源说明</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {metrics.map((item, idx) => (
                    <tr key={item.id || idx} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-4 font-mono font-semibold text-foreground-900">
                        {formatDate(item.stat_date || item.created_at)}
                      </td>
                      <td className="py-3 px-4 text-foreground-700">
                        {item.episode_no != null ? `第 ${item.episode_no} 集` : '-'}
                      </td>
                      <td className="py-3 px-4 text-foreground-600">{item.region_code || '-'}</td>
                      <td className="py-3 px-4 text-right font-semibold text-foreground-950">
                        {formatNumber(item.play_count)}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground-800">
                        {formatNumber(item.like_count)}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground-800">
                        {formatNumber(item.comment_count)}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground-800">
                        {formatNumber(item.favorite_count)}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground-800">
                        {formatNumber(item.conversion_count)}
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-600 font-semibold">
                        {item.completion_rate != null ? `${(item.completion_rate * 100).toFixed(1)}%` : '-'}
                      </td>
                      <td className="py-3 px-4 text-foreground-400 max-w-[180px] truncate" title={item.data_source_note}>
                        {item.data_source_note || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
